import { Brand } from '../../enum/brand';
import { MileageDataSource } from '../../enum/mileageDataSource';
import { ValueUpdater } from '../../valueUpdater';
import { EquipmentOption } from './equipmentOption';
export class Vehicle {
    static fromRawData(rawData) {
        var _a, _b;
        const vehicleState = rawData.vehicleState;
        const vehicleSpec = vehicleState.vehicleSpecification;
        const typeInfo = vehicleSpec.typeInformation;
        const vinShort = vehicleSpec.vehicleIdentification.vinShort;
        const vinLongPrefix = vehicleSpec.vehicleIdentification.vinLongPrefix;
        const vehicle = new this(vinLongPrefix + vinShort);
        vehicle.brand = this.getBrandFromRawData(vehicleSpec.brand);
        vehicle.vehicleType = this.getVehicleTypeFromRawData(vehicleSpec.prodArt);
        vehicle.steering = this.getSteeringTypeFromRawData(typeInfo.steering, vehicle.brand);
        vehicle.transmissionType = this.getTransmissionTypeFromRawData(typeInfo.gear);
        vehicle.driveType = this.getDriveTypeFromRawData(typeInfo.drive);
        if ((_a = vehicleState.firstRegistration) === null || _a === void 0 ? void 0 : _a.firstRegistrationDate) {
            vehicle.firstRegistrationDate = new Date(vehicleState.firstRegistration.firstRegistrationDate.split('[')[0]);
        }
        const paintEquipment = EquipmentOption.extractFirstFromRawData(vehicleState.specialEquipments, 'L');
        vehicle.paintNumber = paintEquipment === null || paintEquipment === void 0 ? void 0 : paintEquipment.getCode();
        vehicle.paint = paintEquipment === null || paintEquipment === void 0 ? void 0 : paintEquipment.getDescription();
        const upholsteryEquipment = EquipmentOption.extractFirstFromRawData(vehicleState.specialEquipments, 'P');
        vehicle.upholsteryCode = upholsteryEquipment === null || upholsteryEquipment === void 0 ? void 0 : upholsteryEquipment.getCode();
        vehicle.upholstery = upholsteryEquipment === null || upholsteryEquipment === void 0 ? void 0 : upholsteryEquipment.getDescription();
        vehicle.equipmentOptions = EquipmentOption.fromRawData(vehicleState.specialEquipments);
        vehicle.baseTypeCode = typeInfo.baseType;
        vehicle.typeCode = typeInfo.type;
        vehicle.currentMileageInKilometers = vehicle.getVin7() ? (_b = vehicleState.mileage) === null || _b === void 0 ? void 0 : _b.mileage : null;
        vehicle.mileageSource = vehicleState.mileage ? MileageDataSource.VEHICLE_HISTORY : null;
        vehicle.modelDesignation = typeInfo.model;
        vehicle.productionDate = new Date(vehicleSpec.productionDate.split('[')[0]);
        if (vehicleSpec.warrantyStart) {
            vehicle.warrantyStartDate = new Date(vehicleSpec.warrantyStart.split('[')[0]);
        }
        if (vehicleState.ILevel) {
            vehicle.integrationLevelCurrent = Vehicle.getStringOrNull(vehicleState.ILevel.integrationLevel);
        }
        else {
            vehicle.integrationLevelCurrent = null;
        }
        vehicle.integrationLevelExWorks = Vehicle.getStringOrNull(vehicleSpec.ILevelExworks);
        vehicle.engineNumber = vehicleSpec.engineNumber;
        vehicle.developmentSeries = vehicleSpec.eseries;
        vehicle.transmissionNumber = vehicleSpec.gearNumber;
        vehicle.imageUrlSmall = rawData.imageUrlSmall ? rawData.imageUrlSmall : null;
        vehicle.imageUrlLarge = rawData.imageUrlLarge ? rawData.imageUrlLarge : null;
        vehicle.capacity = Vehicle.getPositiveNumberOrNull(typeInfo.cubicCapacity);
        vehicle.capacityUnit = 'l';
        vehicle.power = typeInfo.power;
        vehicle.powerUnit = 'kw';
        vehicle.validFrom = null;
        vehicle.validUntil = null;
        vehicle.bodyType = typeInfo.bodyType;
        vehicle.engineType = Vehicle.getStringOrNull(typeInfo.engine);
        vehicle.marketVersion = Vehicle.getStringOrNull(typeInfo.country);
        vehicle.leadType = typeInfo.leadType;
        vehicle.modelSeries = null;
        vehicle.electricMotor = null;
        vehicle.rawData = rawData;
        return vehicle;
    }
    static createVirtualVehicle(rawData) {
        const vehicle = new this(undefined);
        vehicle.brand = this.mapBrand(rawData.typeAttributes.brand[0].searchTerm);
        vehicle.modelSeries = rawData.typeAttributes.modelSeries[0].displayTerm;
        vehicle.developmentSeries = rawData.typeAttributes.developmentCode[0].displayTerm;
        vehicle.modelDesignation = rawData.typeAttributes.model[0].displayTerm;
        vehicle.marketVersion = rawData.typeAttributes.market[0].displayTerm;
        if (vehicle.brand === Brand.MOT) {
            vehicle.vehicleType = "MOTORCYCLE";
            vehicle.steering = "MOTORCYCLE";
        }
        else {
            vehicle.vehicleType = "PASSENGER_CAR";
            vehicle.bodyType = rawData.typeAttributes.body[0].displayTerm;
            vehicle.engineType = rawData.typeAttributes.engine[0].displayTerm;
            vehicle.electricMotor = rawData.typeAttributes.electricMotor[0].displayTerm;
            vehicle.steering = this.mapSteering(rawData.typeAttributes.steering[0].searchTerm);
            vehicle.transmissionType = this.mapTransmission(rawData.typeAttributes.transmission[0].searchTerm);
        }
        vehicle.typeCode = rawData.typeCodes[0].typekey;
        vehicle.baseTypeCode = rawData.typeCodes[0].baseType;
        vehicle.capacity = rawData.typeCodes[0].capacity;
        vehicle.capacityUnit = rawData.typeCodes[0].capacityUnit;
        vehicle.power = rawData.typeCodes[0].power;
        vehicle.powerUnit = rawData.typeCodes[0].powerUnit;
        vehicle.validFrom = rawData.typeCodes[0].validFrom;
        vehicle.validUntil = rawData.typeCodes[0].validUntil;
        vehicle.imageUrlSmall = rawData.typeCodes[0].smallCosyImageUrl;
        vehicle.imageUrlLarge = rawData.typeCodes[0].largeCosyImageUrl;
        return vehicle;
    }
    static getBrandFromRawData(brandFromRawData) {
        let brand = Brand.UNKNOWN;
        if (brandFromRawData in Brand) {
            brand = Brand[brandFromRawData];
        }
        else if (brandFromRawData === 'BMW_I') {
            brand = Brand.BMW_i;
        }
        return brand;
    }
    static getVehicleTypeFromRawData(vehicleTypeFromRawData) {
        let vehicleType = "UNKNOWN";
        if (vehicleTypeFromRawData === '1') {
            vehicleType = "PASSENGER_CAR";
        }
        else if (vehicleTypeFromRawData === '0') {
            vehicleType = "MOTORCYCLE";
        }
        return vehicleType;
    }
    static getSteeringTypeFromRawData(steeringTypeFromRawData, brand) {
        let steeringType = "UNKNOWN";
        if (brand === Brand.MOT) {
            steeringType = "MOTORCYCLE";
        }
        else if (steeringTypeFromRawData === 'LL') {
            steeringType = "LEFT_HAND_DRIVE";
        }
        else if (steeringTypeFromRawData === 'RL') {
            steeringType = "RIGHT_HAND_DRIVE";
        }
        return steeringType;
    }
    static getTransmissionTypeFromRawData(transmissionTypeFromRawData) {
        let transmissionType = "UNKNOWN";
        if (transmissionTypeFromRawData === 'AUT') {
            transmissionType = "AUTOMATIC_TRANSMISSION";
        }
        else if (transmissionTypeFromRawData === 'MECH') {
            transmissionType = "MANUAL_TRANSMISSION";
        }
        return transmissionType;
    }
    static getDriveTypeFromRawData(driveTypeFromRawData) {
        let driveType = "UNKNOWN";
        if (driveTypeFromRawData === 'ALLR') {
            driveType = "ALL_WHEEL_DRIVE";
        }
        else if (driveTypeFromRawData === 'FRONT') {
            driveType = "FRONT_WHEEL_DRIVE";
        }
        else if (driveTypeFromRawData === 'HECK') {
            driveType = "REAR_WHEEL_DRIVE";
        }
        else if (driveTypeFromRawData === 'KAR') {
            driveType = "SHAFT_DRIVE";
        }
        return driveType;
    }
    static mapBrand(brand) {
        let virtualVehicleBrand = Brand.UNKNOWN;
        if (brand === 'BMW') {
            virtualVehicleBrand = Brand.BMW;
        }
        else if (brand === 'BMW MOTORRAD') {
            virtualVehicleBrand = Brand.MOT;
        }
        else if (brand === 'BMWi') {
            virtualVehicleBrand = Brand.BMW_i;
        }
        else if (brand === 'MINI') {
            virtualVehicleBrand = Brand.MINI;
        }
        else if (brand === 'ROLLS ROYCE') {
            virtualVehicleBrand = Brand.RR;
        }
        else if (brand === 'ZINORO') {
            virtualVehicleBrand = Brand.ZINORO;
        }
        return virtualVehicleBrand;
    }
    static mapSteering(steering) {
        let virtualVehicleSteering = "UNKNOWN";
        if (steering === 'LL') {
            virtualVehicleSteering = "LEFT_HAND_DRIVE";
        }
        else if (steering === 'RL') {
            virtualVehicleSteering = "RIGHT_HAND_DRIVE";
        }
        return virtualVehicleSteering;
    }
    static mapTransmission(transmission) {
        let virtualVehicleTransmission = "UNKNOWN";
        if (transmission === 'AUT') {
            virtualVehicleTransmission = "AUTOMATIC_TRANSMISSION";
        }
        else if (transmission === 'MECH') {
            virtualVehicleTransmission = "MANUAL_TRANSMISSION";
        }
        return virtualVehicleTransmission;
    }
    static getStringOrNull(field) {
        return field && field !== '' ? field : null;
    }
    static getPositiveNumberOrNull(field) {
        return field && field > 0 ? field : null;
    }
    constructor(vin) {
        this.licensePlate = null;
        this.vehicleType = "UNKNOWN";
        this.brand = Brand.UNKNOWN;
        this.modelDesignation = null;
        this.steering = "UNKNOWN";
        this.transmissionType = "UNKNOWN";
        this.driveType = "UNKNOWN";
        this.baseTypeCode = null;
        this.typeCode = null;
        this.mileageSource = null;
        this.equipmentOptions = [];
        this.developmentSeries = null;
        this.imageUrlSmall = null;
        this.imageUrlLarge = null;
        this.capacity = null;
        this.capacityUnit = null;
        this.power = null;
        this.powerUnit = null;
        this.validFrom = null;
        this.validUntil = null;
        this.bodyType = null;
        this.engineType = null;
        this.marketVersion = null;
        this.modelSeries = null;
        this.electricMotor = null;
        if (vin) {
            if (vin.length === 17) {
                this.vin = vin;
                this.vin7 = vin.slice(vin.length - 7);
            }
            else {
                throw new Error(`Invalid VIN. Expected 17 digits but received ${vin.length} digits instead.`);
            }
        }
        else {
            this.vin = undefined;
            this.vin7 = undefined;
        }
    }
    getTypeCode() {
        return this.typeCode;
    }
    getBaseTypeCode() {
        return this.baseTypeCode;
    }
    getBrand() {
        return this.brand;
    }
    getEquipmentOptions() {
        return this.equipmentOptions;
    }
    getModelDesignation() {
        return this.modelDesignation;
    }
    getSteering() {
        return this.steering;
    }
    getTransmissionType() {
        return this.transmissionType;
    }
    getDriveType() {
        return this.driveType;
    }
    getProductionDate() {
        return this.productionDate;
    }
    getFirstRegistrationDate() {
        return this.firstRegistrationDate;
    }
    getWarrantyStartDate() {
        return this.warrantyStartDate;
    }
    getVehicleType() {
        return this.vehicleType;
    }
    getVin() {
        return this.vin;
    }
    getVin7() {
        return this.vin7;
    }
    getLicensePlate() {
        return this.licensePlate;
    }
    setLicensePlate(licensePlate) {
        this.licensePlate = licensePlate;
    }
    getPaint() {
        return this.paint;
    }
    getCurrentMileageInKilometers() {
        return this.currentMileageInKilometers;
    }
    setCurrentMileageInKilometers(mileage) {
        this.currentMileageInKilometers = mileage;
    }
    getMileageSource() {
        return this.mileageSource;
    }
    setMileageSource(source) {
        this.mileageSource = source;
    }
    getPaintNumber() {
        return this.paintNumber;
    }
    getUpholstery() {
        return this.upholstery;
    }
    getUpholsteryCode() {
        return this.upholsteryCode;
    }
    getCapacity() {
        return this.capacity;
    }
    getCapacityUnit() {
        return this.capacityUnit;
    }
    getDevelopmentSeries() {
        return this.developmentSeries;
    }
    getEngineNumber() {
        return this.engineNumber;
    }
    getImageUrlLarge() {
        return this.imageUrlLarge;
    }
    getImageUrlSmall() {
        return this.imageUrlSmall;
    }
    getIntegrationLevelCurrent() {
        return this.integrationLevelCurrent;
    }
    getIntegrationLevelExWorks() {
        return this.integrationLevelExWorks;
    }
    getPower() {
        return this.power;
    }
    getPowerUnit() {
        return this.powerUnit;
    }
    getTransmissionNumber() {
        return this.transmissionNumber;
    }
    getValidFrom() {
        return this.validFrom;
    }
    getValidUntil() {
        return this.validUntil;
    }
    getBodyType() {
        return this.bodyType;
    }
    getEngineType() {
        return this.engineType;
    }
    getMarketVersion() {
        return this.marketVersion;
    }
    getLeadType() {
        return this.leadType;
    }
    getModelSeries() {
        return this.modelSeries;
    }
    getElectricMotor() {
        return this.electricMotor;
    }
}
Vehicle.fromPlainObject = (sourceVehicle) => {
    let targetVehicle = new Vehicle(sourceVehicle.vin);
    targetVehicle = Object.assign(targetVehicle, sourceVehicle);
    targetVehicle.licensePlate = ValueUpdater.updateNullableString(targetVehicle.licensePlate, sourceVehicle.licensePlate);
    targetVehicle.vehicleType = ValueUpdater.updateNonNullableField(targetVehicle.vehicleType, sourceVehicle.vehicleType);
    targetVehicle.brand = ValueUpdater.updateNonNullableField(targetVehicle.brand, sourceVehicle.brand);
    targetVehicle.modelDesignation = ValueUpdater.updateNullableString(targetVehicle.modelDesignation, sourceVehicle.modelDesignation);
    targetVehicle.steering = ValueUpdater.updateNonNullableField(targetVehicle.steering, sourceVehicle.steering);
    targetVehicle.transmissionType = ValueUpdater.updateNonNullableField(targetVehicle.transmissionType, sourceVehicle.transmissionType);
    targetVehicle.driveType = ValueUpdater.updateNonNullableField(targetVehicle.driveType, sourceVehicle.driveType);
    targetVehicle.productionDate = ValueUpdater.getDateValue(sourceVehicle.productionDate);
    targetVehicle.firstRegistrationDate = ValueUpdater.getDateValue(sourceVehicle.firstRegistrationDate);
    targetVehicle.warrantyStartDate = ValueUpdater.getDateValue(sourceVehicle.warrantyStartDate);
    targetVehicle.baseTypeCode = ValueUpdater.updateNullableString(targetVehicle.baseTypeCode, sourceVehicle.baseTypeCode);
    targetVehicle.typeCode = ValueUpdater.updateNullableString(targetVehicle.typeCode, sourceVehicle.typeCode);
    targetVehicle.currentMileageInKilometers = sourceVehicle.currentMileageInKilometers;
    targetVehicle.mileageSource = ValueUpdater.updateNullableField(targetVehicle.mileageSource, sourceVehicle.mileageSource);
    targetVehicle.paint = sourceVehicle.paint;
    targetVehicle.paintNumber = sourceVehicle.paintNumber;
    targetVehicle.upholstery = sourceVehicle.upholstery;
    targetVehicle.upholsteryCode = sourceVehicle.upholsteryCode;
    targetVehicle.equipmentOptions = EquipmentOption.fromPlainObjectList(sourceVehicle.equipmentOptions);
    targetVehicle.integrationLevelCurrent = sourceVehicle.integrationLevelCurrent;
    targetVehicle.integrationLevelExWorks = sourceVehicle.integrationLevelExWorks;
    targetVehicle.engineNumber = sourceVehicle.engineNumber;
    targetVehicle.developmentSeries = ValueUpdater.updateNullableString(targetVehicle.developmentSeries, sourceVehicle.developmentSeries);
    targetVehicle.transmissionNumber = sourceVehicle.transmissionNumber;
    targetVehicle.imageUrlSmall = ValueUpdater.updateNullableString(targetVehicle.imageUrlSmall, sourceVehicle.imageUrlSmall);
    targetVehicle.imageUrlLarge = ValueUpdater.updateNullableString(targetVehicle.imageUrlLarge, sourceVehicle.imageUrlLarge);
    targetVehicle.capacity = ValueUpdater.updateNullableNumber(targetVehicle.capacity, sourceVehicle.capacity);
    targetVehicle.capacityUnit = ValueUpdater.updateNullableString(targetVehicle.capacityUnit, sourceVehicle.capacityUnit);
    targetVehicle.power = ValueUpdater.updateNullableNumber(targetVehicle.power, sourceVehicle.power);
    targetVehicle.powerUnit = ValueUpdater.updateNullableString(targetVehicle.powerUnit, sourceVehicle.powerUnit);
    targetVehicle.validFrom = ValueUpdater.updateNullableString(targetVehicle.validFrom, sourceVehicle.validFrom);
    targetVehicle.validUntil = ValueUpdater.updateNullableString(targetVehicle.validUntil, sourceVehicle.validUntil);
    targetVehicle.bodyType = ValueUpdater.updateNullableString(targetVehicle.bodyType, sourceVehicle.bodyType);
    targetVehicle.engineType = ValueUpdater.updateNullableString(targetVehicle.engineType, sourceVehicle.engineType);
    targetVehicle.marketVersion = ValueUpdater.updateNullableString(targetVehicle.marketVersion, sourceVehicle.marketVersion);
    targetVehicle.leadType = sourceVehicle.leadType;
    targetVehicle.modelSeries = ValueUpdater.updateNullableString(targetVehicle.modelSeries, sourceVehicle.modelSeries);
    targetVehicle.electricMotor = ValueUpdater.updateNullableString(targetVehicle.electricMotor, sourceVehicle.electricMotor);
    targetVehicle.rawData = sourceVehicle.rawData;
    return targetVehicle;
};
//# sourceMappingURL=vehicle.js.map